import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "reactstrap";

import getCurrencySymbol from "../../utils/getCurrencySymbol";

interface CurrencyBreakdownItem {
  currency: string;
  balance: number;
}

interface StateSummaryProps {
  balance?: number;
  numTrades?: number;
  numBots?: number;
  deposit?: number;
  currency?: string;
  withdrawal?: number;
  hasMultipleCurrencies?: boolean;
  currencyBreakdown?: CurrencyBreakdownItem[];
  isRefreshing?: boolean;
  isLoading?: boolean;
  /** True when the stats request failed. Values are then unknown, not zero. */
  hasError?: boolean;
  onRetry?: () => void;
}

const StateSummary = ({
  balance,
  numTrades,
  numBots,
  deposit,
  currency,
  withdrawal,
  hasMultipleCurrencies,
  currencyBreakdown = [],
  isRefreshing = false,
  isLoading = false,
  hasError = false,
  onRetry,
}: StateSummaryProps) => {
  const { t, i18n } = useTranslation("stats", { keyPrefix: "stateSummary" });
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const symbol = useMemo(() => getCurrencySymbol(currency || ""), [currency]);

  // Calculate net flow for indicator (used for potential future indicator)

  // An unknown value must not render as 0.00: a failed request used to be
  // presented as a zeroed portfolio.
  const formatNumber = (num: number | undefined) => {
    if (hasError && num === undefined) return "—";
    return (num || 0).toLocaleString(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCount = (num: number | undefined) => (hasError && num === undefined ? "—" : (num ?? 0));
  /** Blank when the value is unknown, so the page never shows a bare "— €". */
  const unitFor = (num: number | undefined) => (hasError && num === undefined ? "" : symbol);

  return (
    <div className="stat-hero mb-4">
      <div className="stat-hero-content">
        {/* Label with Mixed Currency Indicator */}
        <div className="stat-hero-label" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {t("totalBalance")}
          {isRefreshing && (
            <i
              className="fas fa-sync-alt fa-spin"
              style={{
                fontSize: "0.65rem",
                opacity: 0.6,
              }}
            />
          )}
          {hasMultipleCurrencies && (
            <>
              <span
                id="mixed-currency-indicator"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  backgroundColor: "rgba(251, 191, 36, 0.2)",
                  color: "#fbbf24",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <i className="fas fa-exchange-alt" style={{ fontSize: "0.6rem" }} />
                {t("mixedCurrencies")}
              </span>
              <Tooltip
                placement="bottom"
                isOpen={tooltipOpen}
                target="mixed-currency-indicator"
                toggle={() => setTooltipOpen(!tooltipOpen)}
                style={{
                  maxWidth: "320px",
                  textAlign: "left",
                }}
                innerClassName="currency-breakdown-tooltip"
              >
                <div style={{ padding: "8px 4px" }}>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: "12px",
                      fontSize: "0.85rem",
                      borderBottom: "1px solid rgba(255,255,255,0.2)",
                      paddingBottom: "8px",
                    }}
                  >
                    {t("currencyBreakdown")}
                  </div>
                  {currencyBreakdown.map(({ currency: curr, balance: bal }) => (
                    <div
                      key={curr}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 8px",
                        marginBottom: "4px",
                        backgroundColor: "rgba(255,255,255,0.05)",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                      }}
                    >
                      <span style={{ color: "#67e8f9", fontWeight: 600 }}>{curr}</span>
                      <span style={{ fontWeight: 500 }}>
                        {formatNumber(bal)} {getCurrencySymbol(curr)}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      fontSize: "0.7rem",
                      opacity: 0.6,
                      marginTop: "8px",
                      fontStyle: "italic",
                    }}
                  >
                    {t("convertedToMain", { currency })}
                  </div>
                </div>
              </Tooltip>
            </>
          )}
        </div>

        {hasError && !isLoading && (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.75rem",
              marginBottom: 6,
              color: "#fde68a",
            }}
          >
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <span>{t("loadError")}</span>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  border: "1px solid rgba(255,255,255,0.4)",
                  background: "transparent",
                  color: "inherit",
                  borderRadius: 4,
                  padding: "1px 8px",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                }}
              >
                {t("retry")}
              </button>
            )}
          </div>
        )}

        {/* Main Value */}
        <div className="stat-hero-value">
          {isLoading ? (
            <span style={{ opacity: 0.5 }}>...</span>
          ) : (
            <>
              {formatNumber(balance)} {unitFor(balance)}
            </>
          )}
        </div>

        {/* Stats Grid */}
        <div className="stat-hero-grid">
          <div className="stat-hero-item">
            <div className="label">{t("totalDeposit")}</div>
            <div className="value" style={{ color: "#ffffff" }}>
              {isLoading ? (
                "..."
              ) : (
                <>
                  {formatNumber(deposit)} {unitFor(deposit)}
                </>
              )}
            </div>
          </div>

          <div className="stat-hero-item">
            <div className="label">{t("totalWithdrawal")}</div>
            <div className="value" style={{ color: "#ffffff" }}>
              {isLoading ? (
                "..."
              ) : (
                <>
                  {formatNumber(withdrawal)} {unitFor(withdrawal)}
                </>
              )}
            </div>
          </div>

          <div className="stat-hero-item">
            <div className="label">{t("openTrades")}</div>
            <div className="value" style={{ color: "#ffffff" }}>
              {isLoading ? "..." : formatCount(numTrades)}
            </div>
          </div>

          <div className="stat-hero-item">
            <div className="label">{t("activeBots")}</div>
            <div className="value" style={{ color: "#67e8f9" }}>
              {isLoading ? "..." : formatCount(numBots)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateSummary;
